import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, useScroll, useSpring } from 'framer-motion';

// Shared easing curve for the public site (smooth "expo-out" feel)
export const EASE = [0.22, 1, 0.36, 1];

/* ---------------------------------- Reveal ---------------------------------- */
// Fades + rises content into view when scrolled to. The workhorse scroll effect.
export const Reveal = ({ children, delay = 0, y = 28, once = true, className = '', ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: '-60px' }}
    transition={{ duration: 0.7, delay, ease: EASE }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

/* ------------------------------ Stagger groups ------------------------------ */
export const Stagger = ({ children, className = '', delay = 0, gap = 0.08 }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: '-60px' }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: gap, delayChildren: delay } } }}
    className={className}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = '', y = 24, ...rest }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y },
      show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
    }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

/* ----------------------------- Kinetic headline ----------------------------- */
// Splits a headline into words that rise out of a clipped line, one after another.
export const KineticText = ({ text, className = '', delay = 0, stagger = 0.05, once = true }) => {
  const words = String(text).split(' ');
  return (
    <motion.span
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once, margin: '-40px' }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      aria-label={text}
    >
      {words.map((word, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em]" aria-hidden="true">
          <motion.span
            className="inline-block"
            variants={{
              hidden: { y: '110%' },
              show: { y: 0, transition: { duration: 0.7, ease: EASE } },
            }}
          >
            {word}
          </motion.span>
          {i < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </motion.span>
  );
};

/* ------------------------------ Rotating word ------------------------------- */
// Cycles through words with a vertical roll — kinetic typography accent.
export const RotatingWord = ({ words, interval = 2600, className = '' }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setIndex((v) => (v + 1) % words.length), interval);
    return () => clearInterval(timer);
  }, [words.length, interval]);

  return (
    <span className={`relative inline-grid overflow-hidden align-bottom pb-[0.2em] -mb-[0.2em] ${className}`}>
      {/* Invisible longest word keeps the box width stable */}
      <span className="invisible col-start-1 row-start-1 whitespace-nowrap">
        {words.reduce((a, b) => (a.length >= b.length ? a : b))}
      </span>
      <AnimatePresence initial={false}>
        <motion.span
          key={words[index]}
          className="col-start-1 row-start-1 whitespace-nowrap"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '-100%', opacity: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

/* --------------------------------- Count up --------------------------------- */
export const CountUp = ({ to, suffix = '', prefix = '', duration = 1.8, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * to));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {value}
      {suffix}
    </span>
  );
};

/* --------------------------------- Marquee ---------------------------------- */
// Infinite horizontal scroll strip; content is duplicated for a seamless loop.
export const Marquee = ({ children, className = '', reverse = false, speed = 45 }) => (
  <div className={`group relative flex overflow-hidden ${className}`}>
    <div
      className="flex w-max shrink-0 animate-marquee items-center group-hover:[animation-play-state:paused]"
      style={{ animationDuration: `${speed}s`, animationDirection: reverse ? 'reverse' : 'normal' }}
    >
      {children}
      {children}
    </div>
  </div>
);

/* ------------------------------ Section heading ----------------------------- */
// Bracket eyebrow echoes the [appsytec] logo mark.
export const Eyebrow = ({ children, dark = false }) => (
  <span
    className={`inline-flex items-baseline gap-1.5 font-display text-xs md:text-sm font-bold uppercase tracking-[0.25em] ${
      dark ? 'text-red-400' : 'text-red-500'
    }`}
  >
    <span aria-hidden="true">[</span>
    {children}
    <span aria-hidden="true">]</span>
  </span>
);

export const SectionHeading = ({ eyebrow, title, subtitle, dark = false, align = 'center', className = '' }) => {
  const alignClass = align === 'left' ? 'text-left items-start' : 'text-center items-center';
  return (
    <div className={`flex flex-col gap-4 ${alignClass} ${className}`}>
      {eyebrow && (
        <Reveal y={16}>
          <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
        </Reveal>
      )}
      <h2
        className={`font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.08] ${
          dark ? 'text-white' : 'text-gray-900'
        }`}
      >
        <KineticText text={title} />
      </h2>
      {subtitle && (
        <Reveal delay={0.15} y={18}>
          <p className={`max-w-2xl text-base md:text-xl ${dark ? 'text-gray-400' : 'text-gray-600'}`}>{subtitle}</p>
        </Reveal>
      )}
    </div>
  );
};

/* ----------------------------- Scroll progress ------------------------------ */
export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 25, restDelta: 0.001 });
  return (
    <motion.div
      className="fixed inset-x-0 top-0 z-[60] h-[3px] origin-left bg-gradient-to-r from-red-500 via-red-400 to-red-500"
      style={{ scaleX }}
    />
  );
};
